/**
 * Time Entry-Specific Actions
 * Actions defined specifically for the time entries entity
 * Strongly typed to Time_Entries row type
 * Uses universal actions and entity-specific builders
 */

import type { Time_Entries } from "@/src/gql/graphql";
import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import { ACTION_ICONS, ACTION_VARIANTS, ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import {
  universalDeleteAction,
  universalEditAction,
  universalSendEmailAction,
  universalLogActivityAction,
  universalAskMeridianAction,
  universalViewDetailsAction,
  universalViewCompanyAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";

/**
 * Complete time entry actions array
 * Combines universal and time entry-specific actions
 */
export const timeEntryDetailActions: DetailActionStrict<Time_Entries>[] = [
  // AI ASSISTANT ACTIONS
  universalAskMeridianAction<Time_Entries>(),

  // PRIMARY ACTIONS (Header) - marked as primary to show in right column
  {
    id: "edit",
    label: "Edit",
    icon: ACTION_ICONS.EDIT,
    variant: ACTION_VARIANTS.OUTLINE,
    primary: true,
    action: "openEditModal",
  },
  {
    id: "quick-approve",
    label: (item: Time_Entries) => (item.is_approved ? "Disapprove" : "Approve"),
    icon: ACTION_ICONS.CHECK_CIRCLE,
    variant: ACTION_VARIANTS.OUTLINE,
    primary: true,
    action: "approveTimeEntry",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "sync-quickbooks",
    label: "Sync to QuickBooks",
    icon: "refresh-cw",
    variant: ACTION_VARIANTS.OUTLINE,
    primary: true,
    action: "syncToQuickBooks",
  },

  // SHARED ACTIONS
  universalViewDetailsAction<Time_Entries>("viewTimeEntry"),

  // SECONDARY ACTIONS (Menu)
  {
    id: "duplicate",
    label: "Duplicate",
    icon: ACTION_ICONS.DUPLICATE,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "duplicateTimeEntry",
  },
  universalViewCompanyAction<Time_Entries>(),
  {
    // Opens the customer's Slack channel, resolved server-side from the time
    // entry's ticket → company (companies.slack_channel_id). Toasts when no
    // channel is set rather than opening a dead tab.
    id: "open-slack-channel",
    label: "Open Slack Channel",
    icon: "message-square",
    variant: ACTION_VARIANTS.OUTLINE,
    action: "openSlackChannel",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    tooltip: "Open this customer's Slack channel to start a conversation.",
  },
  universalConfigureAutomatedTasksAction<Time_Entries>(),
  {
    id: "approve",
    label: (item: Time_Entries) => (item.is_approved ? "Disapprove" : "Approve"),
    icon: ACTION_ICONS.CHECK_CIRCLE,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "approveTimeEntry",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
    bulkable: true,
  },
  {
    id: "copy-link",
    label: "Copy Link",
    icon: ACTION_ICONS.COPY_LINK,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "copyLink",
  },
  {
    id: "reject",
    label: "Reject",
    icon: ACTION_ICONS.X_CIRCLE,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "rejectTimeEntry",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
    bulkable: true,
  },
  {
    id: "move-to-internal-projects",
    label: "Move to Internal Projects",
    icon: "arrow-right",
    variant: ACTION_VARIANTS.OUTLINE,
    action: "moveToInternalProjects",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "reassign-company",
    label: "Reassign Company / Contact",
    icon: "building",
    variant: ACTION_VARIANTS.OUTLINE,
    action: "reassignTicketCompany",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "push-for-approval",
    label: "Push for Approval",
    icon: "send",
    variant: ACTION_VARIANTS.OUTLINE,
    action: "pushTimeEntryForApproval",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
    bulkable: true,
  },
  {
    id: "report-issue",
    label: "Report Issue",
    icon: "alert-circle",
    variant: ACTION_VARIANTS.OUTLINE,
    action: "reportIssue",
  },
  universalDeleteAction<Time_Entries>({ bulkable: true }),
];
